import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./tooltip";
import { cn } from "./utils";

export type ButtonVariant =
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | "warning"
    | "success"
    | "success-secondary"
    | "warning-secondary";

export type ButtonSize = "default" | "sm" | "md" | "lg" | "xl" | "icon";

export interface ButtonVariants {
    variant: {
        [key in ButtonVariant]: string;
    };
    size: {
        [key in ButtonSize]: string;
    };
}

const buttonVariants = cva(
    "group inline-flex flex justify-center justify-center items-center items-center gap-2 gap-x-[6px] disabled:opacity-40 backdrop-blur-lg rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 dark:focus-visible:ring-slate-300 ring-offset-white focus-visible:ring-offset-2 dark:ring-offset-slate-900 dark:ring-offset-slate-950 w-full font-medium text-md whitespace-normal transition-all transition-colors duration-300 disabled:pointer-events-none",
    {
        variants: {
            variant: {
                default:
                    "bg-indigo-600 text-indigo-50 hover:bg-indigo-700 dark:bg-indigo-500 dark:text-white dark:hover:bg-indigo-500/90",
                destructive:
                    "bg-red-500 text-red-50 hover:bg-red-600 dark:bg-red-900 dark:text-slate-50 dark:hover:bg-red-900/90",
                warning:
                    "bg-amber-600 text-amber-50 hover:bg-amber-700 dark:bg-amber-900 dark:text-slate-50 dark:hover:bg-amber-900/90",
                outline:
                    "border border-slate-300 text-slate-800 bg-opacity-50 bg-slate-50 hover:bg-slate-100 hover:text-slate-900 dark:border-slate-500 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800 dark:hover:text-slate-50",
                secondary:
                    "bg-indigo-200 text-indigo-700 hover:text-indigo-800 hover:bg-indigo-300 dark:bg-indigo-900 dark:text-indigo-100 dark:hover:bg-indigo-900/80",
                "destructive-secondary":
                    "bg-red-200 text-red-700 hover:bg-red-300 dark:bg-red-800 dark:text-slate-50 dark:hover:bg-red-900/80",
                ghost: "bg-transparent hover:bg-white/80 hover:text-slate-900 dark:hover:bg-slate-800 dark:text-slate-100 dark:hover:text-slate-50",
                "destructive-ghost":
                    "bg-transparent hover:bg-slate-100 text-red-600 hover:text-red-700 dark:hover:bg-slate-800 dark:text-red-400 dark:hover:text-red-300",
                link: "bg-transparent text-indigo-600 underline-offset-4 underline dark:text-indigo-50 disabled:text-slate-400 hover:bg-transparent",
                "destructive-link": "text-red-600 underline-offset-4 underline dark:text-red-50",
                success:
                    "bg-green-600 text-green-50 hover:bg-green-700 dark:bg-green-700 dark:text-white dark:hover:bg-green-600",
                "success-secondary":
                    "bg-green-200 text-green-700 hover:bg-green-300 dark:bg-green-900 dark:text-green-100 dark:hover:bg-green-800",
                "warning-secondary":
                    "bg-amber-200 text-amber-700 hover:bg-amber-300 dark:bg-amber-900 dark:text-amber-100 dark:hover:bg-amber-800",
            },
            size: {
                default: "min-h-14 h-auto gap-2 rounded-lg px-10 py-3 w-full",
                md: "min-h-12 h-auto gap-2 px-6 py-2 w-fit",
                sm: "min-h-11 h-auto gap-1 text-sm rounded-md px-3 py-2 w-fit",
                lg: "min-h-14 h-auto gap-2 rounded-lg px-10 py-3 w-full",
                xl: "text-xl gap-2 min-h-16 h-auto py-3 rounded-md px-16 w-full",
                icon: "h-10 w-10 rounded-md p-0 whitespace-nowrap",
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    tooltip?: React.ReactNode | false;
    tooltipSide?: React.ComponentPropsWithoutRef<typeof TooltipContent>["side"];
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    ({ className, variant, size, asChild = false, tooltip, tooltipSide = "top", title, ...props }, ref) => {
        const Comp = asChild ? Slot : "button";
        const fallbackTooltip = typeof props["aria-label"] === "string" ? props["aria-label"] : title;
        const tooltipContent = tooltip === false ? null : (tooltip ?? fallbackTooltip);
        const shouldShowTooltip = size === "icon" && !asChild && Boolean(tooltipContent);
        const buttonProps = shouldShowTooltip ? props : { ...props, title };
        const button = <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...buttonProps} />;

        if (!shouldShowTooltip) {
            return button;
        }

        return (
            <TooltipProvider delayDuration={150}>
                <Tooltip>
                    <TooltipTrigger asChild>{button}</TooltipTrigger>
                    <TooltipContent side={tooltipSide}>
                        {typeof tooltipContent === "string" ? <p>{tooltipContent}</p> : tooltipContent}
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }
);
Button.displayName = "Button";

interface ButtonLabelProps {
    children: React.ReactNode;
    icon?: React.ReactNode;
    iconPosition?: "leading" | "trailing";
    className?: string;
}

/** Wraps button copy so it can break, with the icon sitting after the last word. */
function ButtonLabel({ children, icon, iconPosition = "trailing", className }: ButtonLabelProps) {
    const text = typeof children === "string" ? children.trim() : null;
    const labelClassName = cn("min-w-0 text-center text-balance leading-snug", className);

    if (!icon) {
        return <span className={labelClassName}>{children}</span>;
    }

    if (text) {
        const words = text.split(/\s+/);
        if (iconPosition === "leading") {
            return (
                <span className={labelClassName}>
                    <span className="inline-flex flex-nowrap justify-center items-center gap-1">
                        <span className="shrink-0">{icon}</span>
                        <span>{text}</span>
                    </span>
                </span>
            );
        }

        const last = words.pop() ?? "";
        const lead = words.join(" ");
        return (
            <span className={labelClassName}>
                {lead ? `${lead} ` : null}
                <span className="inline-flex items-center gap-1 whitespace-nowrap">
                    {last}
                    {icon}
                </span>
            </span>
        );
    }

    return (
        <span className={labelClassName}>
            {iconPosition === "leading" ? (
                <>
                    {icon} {children}
                </>
            ) : (
                <>
                    {children} {icon}
                </>
            )}
        </span>
    );
}

export { Button, ButtonLabel, buttonVariants };
